/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useEffect, useState } from 'react';
import { hooks, unstable } from '@broadleaf/admin-components';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { LinkWithMiniBrowser } from 'app/common/components';
import { errorPayPal } from 'app/paypal/components/PayPalView/state/actions';
import { validateAccountRedirectUrl } from 'app/paypal/components/PayPalView/utils/PayPalApiUtils';

import messages from './UpdateAccount.messages';

const { useCancelTokenFactory } = hooks;
const { Spinner, useFormatMessage } = unstable;

/**
 * Component that renders the update action. By default it will request a link
 * to the PayPal dashboard and redirect the user to there.
 *
 * @author Nathan Moore (nathandmoore)
 */
const UpdateAccount = ({ disabled = false, dispatch, payPalApi, state }) => {
  const formatMessage = useFormatMessage();
  const [getCancelToken] = useCancelTokenFactory();
  const [url, setUrl] = useState(get(state, 'data.url'));

  useEffect(() => {
    if (state.isFetching || !isEmpty(url) || disabled) {
      return;
    }

    (async () => {
      const response = await payPalApi.accounts.update({
        cancelToken: getCancelToken()
      });

      if (response === undefined) {
        // there was an error, which is handled separately
        setUrl('#');
        return;
      }

      if (validateAccountRedirectUrl(response.url)) {
        setUrl(response.url);
      } else {
        setUrl('#');
        dispatch(
          errorPayPal({
            type: 'INVALID_REDIRECT_URL',
            data: {
              globalErrors: [
                { reason: formatMessage(messages.redirectLinkError) }
              ]
            }
          })
        );
      }
    })();
    // eslint-disable-next-line
  }, [state, disabled]);

  return (
    <>
      {state.isFetching && <Spinner className="tw-mr-2" color="info" />}
      <LinkWithMiniBrowser
        className="w-relative focus:tw-outline-none focus:tw-shadow-outline tw-ml-2 tw-flex tw-inline-flex tw-items-center tw-rounded tw-bg-green-700 tw-py-2 tw-px-4 tw-text-sm tw-text-white tw-shadow hover:tw-bg-green-800 hover:tw-text-white hover:tw-no-underline"
        disabled={disabled}
        url={url}
        height="full"
        width="full"
        target="PayPalFrame"
      >
        {formatMessage(messages.updateAccountLabel)}
      </LinkWithMiniBrowser>
    </>
  );
};

UpdateAccount.propTypes = {
  /** ID of the account to update */
  accountId: PropTypes.string.isRequired,
  /** Whether the button should be disabled */
  disabled: PropTypes.bool,
  /** Interface for interacting with the PayPal API */
  payPalApi: PropTypes.shape({
    accounts: PropTypes.shape({
      update: PropTypes.func.isRequired
    }).isRequired
  }).isRequired
};

export default UpdateAccount;
export { UpdateAccount };
